/**
 * Contact セクション内のネイティブ select について:
 * 先頭の value="" / disabled オプション（ラベル文言の表示）は、
 * 閉じた状態では option 要素への CSS（font-size 等）が適用されないブラウザがほとんど。
 * 空選択の間だけ select にクラスを付け、custom.css で見た目を切り替える。
 */
function bindContactSelectPlaceholderState(section) {
  section.querySelectorAll('select.select').forEach((select) => {
    if (select.dataset.cContactPlaceholderBound === 'true') {
      return;
    }
    select.dataset.cContactPlaceholderBound = 'true';
    const control = select.closest('.form-control');
    const placeholderLabel = select.querySelector('option[value=""]')?.textContent.trim() || '';

    if (control && placeholderLabel) {
      control.dataset.cContactSelectPlaceholder = placeholderLabel;
    }

    const sync = () => {
      const isPlaceholder = select.value === '';
      select.classList.toggle('c-contact_select_placeholder', isPlaceholder);
      control?.classList.toggle('c-contact_select_placeholder_control', isPlaceholder && placeholderLabel !== '');
    };

    select.addEventListener('change', sync);
    select.addEventListener('input', sync);
    sync();
  });
}

function initContactSelectPlaceholderStates() {
  document.querySelectorAll('.shopify-section--contact').forEach(bindContactSelectPlaceholderState);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContactSelectPlaceholderStates);
} else {
  initContactSelectPlaceholderStates();
}

function getVisibleElementHeight(element) {
  if (!element) return 0;

  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);

  return Math.max(0, visibleBottom - visibleTop);
}

function getMeasuredElementHeight(element, fallbackVariableName) {
  if (element) {
    const rect = element.getBoundingClientRect();
    if (rect.height > 0) return rect.height;
  }

  const fallbackValue = getComputedStyle(document.documentElement).getPropertyValue(fallbackVariableName);
  return parseFloat(fallbackValue) || 0;
}

function getHeaderHeightForSidebar(header) {
  const sidebar = header?.querySelector?.('#sidebar-menu');

  if (!sidebar || getComputedStyle(sidebar).display === 'none') {
    return getMeasuredElementHeight(header, '--header-height');
  }

  const previousDisplay = sidebar.style.display;

  // The drawer is still inside the header during dialog:before-show. Hide it
  // while measuring so the header grid row-gap is not counted as header height.
  sidebar.style.display = 'none';
  const height = getMeasuredElementHeight(header, '--header-height');
  sidebar.style.display = previousDisplay;

  return height;
}

function updateHeaderSidebarMetrics() {
  const header = document.querySelector('.shopify-section--header');
  const announcement = document.querySelector('.shopify-section--announcement-bar');
  const headerHeight = getHeaderHeightForSidebar(header);
  const visibleAnnouncementHeight = getVisibleElementHeight(announcement);
  const announcementHeight = visibleAnnouncementHeight > 1 ? visibleAnnouncementHeight : 0;
  const offset = headerHeight + announcementHeight;

  document.documentElement.style.setProperty('--c-header-sidebar-header-height', `${headerHeight.toFixed(2)}px`);
  document.documentElement.style.setProperty('--c-header-sidebar-announcement-height', `${announcementHeight.toFixed(2)}px`);
  document.documentElement.style.setProperty('--c-header-sidebar-announcement-included', announcementHeight > 0 ? '1' : '0');
  document.documentElement.style.setProperty('--c-header-sidebar-offset', `${offset.toFixed(2)}px`);
  document.documentElement.style.removeProperty('--c-header-sidebar-height');
}

function initHeaderSidebarChrome() {
  let scheduled = false;
  const scheduleUpdate = () => {
    if (scheduled) return;

    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      updateHeaderSidebarMetrics();
    });
  };

  updateHeaderSidebarMetrics();

  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
  document.addEventListener(
    'dialog:before-show',
    (event) => {
      if (event.target && event.target.id === 'sidebar-menu') {
        updateHeaderSidebarMetrics();
      }
    },
    true
  );
}

function patchHeaderSidebarOutsideClick() {
  const HeaderSidebar = window.customElements && window.customElements.get('header-sidebar');
  if (!HeaderSidebar || HeaderSidebar.prototype.__cHeaderOutsideClickPatched) return;

  HeaderSidebar.prototype.__cHeaderOutsideClickPatched = true;
  const originalAllowOutsideClickForTarget = HeaderSidebar.prototype.allowOutsideClickForTarget;

  HeaderSidebar.prototype.allowOutsideClickForTarget = function (target) {
    const isSidebarToggle = target && target.closest && target.closest(`[aria-controls="${this.id}"]`);
    if (isSidebarToggle) {
      return originalAllowOutsideClickForTarget
        ? originalAllowOutsideClickForTarget.call(this, target)
        : false;
    }

    if (target && target.closest && target.closest('.shopify-section--header, .shopify-section--announcement-bar')) {
      return true;
    }

    return originalAllowOutsideClickForTarget
      ? originalAllowOutsideClickForTarget.call(this, target)
      : false;
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderSidebarChrome);
} else {
  initHeaderSidebarChrome();
}

if (window.customElements) {
  patchHeaderSidebarOutsideClick();
  window.customElements.whenDefined('header-sidebar').then(patchHeaderSidebarOutsideClick);
}

/**
 * Prestige の product-rerender が `buy-buttons` を `replaceWith(null)` すると要素ごと DOM から消える。
 * 売り切れ・再描画タイミングで購入ボタン枠が空になるのを防ぐ（theme.js は編集しない）。
 */
(function patchBuyButtonsReplaceWith() {
  if (typeof Element === 'undefined' || !Element.prototype.replaceWith) return;
  if (Element.prototype.__cPatchedBuyButtonsReplaceWith) return;
  Element.prototype.__cPatchedBuyButtonsReplaceWith = true;
  const orig = Element.prototype.replaceWith;
  Element.prototype.replaceWith = function (...nodes) {
    if (
      typeof this.matches === 'function' &&
      this.matches('buy-buttons') &&
      (nodes.length === 0 || (nodes.length === 1 && nodes[0] == null))
    ) {
      return;
    }
    return orig.apply(this, nodes);
  };
})();

/**
 * カスタムアコーディオン（.product-form__custom-acordion）の開閉
 * ＋ サイズ表: `[data-size-chart]`（例: メタフィールド custom.product_size_text / product_size_chart）を JS でテーブル化（.js-product-size-chart-table）
 * ＋ .c-acordion__table--size ＋ .table-wrapper のモバイル幅・横スクロール・SimpleBar
 */

const ACCORDION_TABLE_MOBILE_MAX = 740;

/** SimpleBar が head で未実行のときに備え待機 */
function whenSimpleBarAvailable(callback) {
  if (typeof window.SimpleBar === 'function') {
    callback();
    return;
  }
  window.addEventListener(
    'load',
    () => {
      if (typeof window.SimpleBar === 'function') callback();
    },
    { once: true }
  );
  let n = 0;
  const id = window.setInterval(() => {
    if (typeof window.SimpleBar === 'function') {
      window.clearInterval(id);
      callback();
    } else if (++n > 120) {
      window.clearInterval(id);
    }
  }, 25);
}

function destroySimpleBarOnWrapper(wrapper) {
  const SB = window.SimpleBar;
  if (!wrapper || !SB || !SB.instances) return;
  const inst = SB.instances.get(wrapper);
  if (inst && typeof inst.unMount === 'function') {
    inst.unMount();
  }
  wrapper.removeAttribute('data-simplebar');
  wrapper.removeAttribute('data-simplebar-auto-hide');
}

function mountSimpleBarOnWrapper(wrapper) {
  const SB = window.SimpleBar;
  if (!wrapper || !SB) return;
  if (SB.instances && SB.instances.has(wrapper)) {
    const inst = SB.instances.get(wrapper);
    if (inst && typeof inst.recalculate === 'function') {
      inst.recalculate();
    }
    return;
  }
  wrapper.setAttribute('data-simplebar', '');
  wrapper.setAttribute('data-simplebar-auto-hide', 'false');
  try {
    new SB(wrapper, { autoHide: false });
  } catch {
    // 二重初期化など
  }
}

/**
 * .c-acordion__table--size（各 table 要素）。モバイルで .table-wrapper に幅とスクロール、SimpleBar。
 */
function setAcordionTableWrappers(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const sizeTables = scope.querySelectorAll('.c-acordion__table--size');
  const screenWidth = document.documentElement.clientWidth;
  const isMobile = screenWidth <= ACCORDION_TABLE_MOBILE_MAX;
  /** @type {HTMLElement[]} */
  const wrappersToMount = [];

  sizeTables.forEach((table) => {
    const tableWrapper = table.closest('.table-wrapper');
    if (!tableWrapper) return;

    if (isMobile) {
      const containerGutter = getComputedStyle(document.documentElement).getPropertyValue('--container-gutter');
      const gutterValue = parseFloat(containerGutter) || 0;
      const calculatedWidth = screenWidth - gutterValue * 2;

      tableWrapper.style.overflowX = 'scroll';
      tableWrapper.style.overflowY = 'hidden';
      tableWrapper.style.width = `${calculatedWidth}px`;
      tableWrapper.style.maxWidth = `${calculatedWidth}px`;
      tableWrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
      tableWrapper.style.setProperty('scrollbar-gutter', 'stable both-edges');

      table.style.minWidth = '600px';
      table.style.width = '600px';

      wrappersToMount.push(tableWrapper);
    } else {
      tableWrapper.style.overflowX = '';
      tableWrapper.style.overflowY = '';
      tableWrapper.style.width = '';
      tableWrapper.style.maxWidth = '';
      tableWrapper.style.removeProperty('-webkit-overflow-scrolling');
      tableWrapper.style.removeProperty('scrollbar-gutter');
      table.style.minWidth = '';
      table.style.width = '';

      destroySimpleBarOnWrapper(tableWrapper);
    }
  });

  if (wrappersToMount.length === 0) return;

  whenSimpleBarAvailable(() => {
    if (document.documentElement.clientWidth > ACCORDION_TABLE_MOBILE_MAX) return;
    wrappersToMount.forEach((wrapper) => mountSimpleBarOnWrapper(wrapper));
  });
}

/**
 * `data-size-chart` の文字列を table の行に展開（メタフィールドの中身は Liquid 側で渡すだけでよい）
 * 書式: 改行=行・カンマ=列。1行目は th、それ以降 td。処理済みは data-size-chart-built。
 */
function initProductSizeChartTables(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const tables = scope.querySelectorAll(
    'table.js-product-size-chart-table[data-size-chart]:not([data-size-chart-built])'
  );
  let built = false;

  tables.forEach((table) => {
    const raw = table.getAttribute('data-size-chart');
    if (raw == null || String(raw).trim() === '') return;

    const lines = String(raw)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) return;

    table.replaceChildren();
    const tbody = document.createElement('tbody');

    lines.forEach((line, rowIndex) => {
      const cells = line.split(',').map((cell) => cell.trim());
      const tr = document.createElement('tr');
      cells.forEach((cellText) => {
        const isHeader = rowIndex === 0;
        const cell = document.createElement(isHeader ? 'th' : 'td');
        if (isHeader) cell.setAttribute('scope', 'col');
        cell.textContent = cellText;
        tr.appendChild(cell);
      });
      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    table.setAttribute('data-size-chart-built', 'true');
    table.removeAttribute('data-size-chart');
    built = true;
  });

  if (built) {
    setAcordionTableWrappers(root);
  }
}

function runAcordionLayout(root = document) {
  initProductSizeChartTables(root);
  setAcordionTableWrappers(root);
}

/**
 * アコーディオンパネル: [hidden] だと display:none で max-height 制御できないため除去し aria-hidden と CSS で折りたたむ
 * @param {ParentNode|null} root
 */
function initProductFormAccordionPanels(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  scope.querySelectorAll('.product-form__custom-acordion .c-acordion .c-acordion__content').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', el.classList.contains('is-open') ? 'false' : 'true');
  });
}

function toggleCustomAcordion(button) {
  const container = button.closest('.c-acordion');
  if (!container || !container.closest('.product-form__custom-acordion')) return;

  const toggles = [...container.querySelectorAll('button.c-acordion__toggle')];
  const contents = [...container.querySelectorAll('.c-acordion__content')];
  const idx = toggles.indexOf(button);
  if (idx < 0) return;

  const willOpen = !button.classList.contains('is-active');

  toggles.forEach((toggle, i) => {
    const open = i === idx && willOpen;
    toggle.classList.toggle('is-active', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const region = contents[i];
    if (!(region instanceof HTMLElement)) return;
    region.classList.toggle('is-open', open);
    region.setAttribute('aria-hidden', open ? 'false' : 'true');
  });

  queueMicrotask(() => runAcordionLayout(document));
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('button.c-acordion__toggle');
  if (!toggle) return;
  toggleCustomAcordion(toggle);
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.classList.contains('shopify-section--contact')) {
    bindContactSelectPlaceholderState(target);
  }
  if (target && typeof target.querySelectorAll === 'function') {
    initProductFormAccordionPanels(target);
    runAcordionLayout(target);
  } else {
    initProductFormAccordionPanels(document);
    runAcordionLayout(document);
  }
});

/** バリアント変更後の部分再描画のあとでもサイズ表・SimpleBar を再計算（theme.js の variant:change は bubbles） */
document.addEventListener('variant:change', () => {
  queueMicrotask(() => {
    initProductFormAccordionPanels(document);
    runAcordionLayout(document);
  });
});

initProductFormAccordionPanels(document);
runAcordionLayout(document);
window.addEventListener('resize', () => runAcordionLayout(document));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initProductFormAccordionPanels(document);
    runAcordionLayout(document);
  });
}

/**
 * 動的チェックアウトのアンブランドボタン（例: 日本語ロケールの「今すぐ購入」）を、
 * snippets/buy-buttons.liquid の data-c-dynamic-checkout-label（product.general.dynamic_checkout_button）に合わせて書き換える。
 * 開いている shadowRoot のみ走査する（閉じたシャドウ内は変更不可）。
 */
function querySelectorAllDeep(root, selector) {
  const results = [];
  function visit(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return;
    if (typeof node.matches === 'function' && node.matches(selector)) {
      results.push(node);
    }
    const children = node.children;
    if (children) {
      for (let i = 0; i < children.length; i++) {
        visit(children[i]);
      }
    }
    const sr = node.shadowRoot;
    if (sr) visit(sr);
  }
  visit(root);
  return results;
}

function applyDynamicCheckoutButtonLabels() {
  document.querySelectorAll('[data-c-dynamic-checkout-label]').forEach((scope) => {
    const label = scope.getAttribute('data-c-dynamic-checkout-label');
    if (!label) return;
    querySelectorAllDeep(scope, '.shopify-payment-button__button--unbranded').forEach((btn) => {
      if (btn.dataset.cDynamicCheckoutLabelApplied === label) return;
      btn.textContent = label;
      btn.dataset.cDynamicCheckoutLabelApplied = label;
    });
  });
}

let dynamicCheckoutLabelRaf = null;
let dynamicCheckoutMutationObserver = null;

function scheduleApplyDynamicCheckoutButtonLabels() {
  if (dynamicCheckoutLabelRaf != null) return;
  dynamicCheckoutLabelRaf = window.requestAnimationFrame(() => {
    dynamicCheckoutLabelRaf = null;
    applyDynamicCheckoutButtonLabels();
  });
}

/** data-c-dynamic-checkout-label があるページだけ subtree を監視し、描画遅延・セクション再読込に追随する */
function ensureDynamicCheckoutMutationObserver() {
  if (dynamicCheckoutMutationObserver) return;
  if (!document.querySelector('[data-c-dynamic-checkout-label]')) return;
  dynamicCheckoutMutationObserver = new MutationObserver(scheduleApplyDynamicCheckoutButtonLabels);
  dynamicCheckoutMutationObserver.observe(document.documentElement, { childList: true, subtree: true });
}

ensureDynamicCheckoutMutationObserver();
scheduleApplyDynamicCheckoutButtonLabels();
document.addEventListener('DOMContentLoaded', () => {
  ensureDynamicCheckoutMutationObserver();
  scheduleApplyDynamicCheckoutButtonLabels();
});
window.addEventListener('load', scheduleApplyDynamicCheckoutButtonLabels);
document.addEventListener('shopify:section:load', () => {
  ensureDynamicCheckoutMutationObserver();
  scheduleApplyDynamicCheckoutButtonLabels();
});
document.addEventListener('variant:change', scheduleApplyDynamicCheckoutButtonLabels);

/**
 * Custom Brand KV:
 * - ロゴ: ページ読み込み完了から1秒後にフェード＋ブラー表示（PC/SP共通）
 * - PC: 見出し・リッチテキストは transition-delay でロゴ→見出し→テキストの順に表示（custom.css）
 * - SP: 見出し・リッチテキストは .c-animate_blur-in で表示領域に入ったら表示
 */
const C_CUSTOM_BRAND_KV_LOGO_REVEAL_DELAY = 1000;

/**
 * @param {Element} section
 */
function cCustomBrandKvScheduleLogoReveal(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.dataset.cCustomBrandKvLogoRevealBound === 'true') return;

  section.dataset.cCustomBrandKvLogoRevealBound = 'true';

  const reveal = () => {
    window.setTimeout(() => {
      section.classList.add('c-custom-brand-kv--logo-visible');
    }, C_CUSTOM_BRAND_KV_LOGO_REVEAL_DELAY);
  };

  if (document.readyState === 'complete') {
    reveal();
    return;
  }

  window.addEventListener('load', reveal, { once: true });
}

/**
 * @param {ParentNode|Document} [root]
 */
function initCustomBrandKvLogoReveal(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const sections = [];

  if (scope instanceof Element && scope.matches('.c-custom-brand-kv')) {
    sections.push(scope);
  }

  scope
    .querySelectorAll('.c-custom-brand-kv:not([data-c-custom-brand-kv-logo-reveal-bound])')
    .forEach((section) => {
      sections.push(section);
    });

  sections.forEach(cCustomBrandKvScheduleLogoReveal);
}

initCustomBrandKvLogoReveal(document);
document.addEventListener('DOMContentLoaded', () => initCustomBrandKvLogoReveal(document));
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target && typeof target.querySelectorAll === 'function') {
    initCustomBrandKvLogoReveal(target);
  }
});

/**
 * スクロールリビール（Intersection Observer）
 *
 * 目的: ビューポートに入ったタイミングで1回だけアニメーションを発火する。
 *
 * 1) .c-animate_blur-in-chars / .c-animate_blur-in
 *    - 要素内のテキスト（または data-c-animate-blur-text）を1文字ずつ span に分解し、
 *      ブラー＋透明度＋スケールのインアニメーションを遅延スタガーで適用する。
 *    - HTML子要素は可能な範囲で維持し、<br> は改行として保持する。
 *    - .c-animate_blur-in はテキスト分割せず、要素全体の opacity と blur だけを同じIO条件で解除する。
 *    - 任意属性: data-c-animate-immediate（trueで即時発火）, data-c-animate-blur-stagger（秒・既定 0.05）, data-c-animate-blur-duration（秒・既定 0.8）
 *
 * 2) .c-animate_fade-blur
 *    - 要素単体で opacity と blur がその場で解ける。発火時に .c-animate_is-in-view を付与する。
 *    - パターン1より厳しい IO 条件（画面中央寄りの帯＋交差率）で、境界ギリギリでは発火しない。
 *    - 既定のイージング・時間は .c-animate_fade-blur の CSS 変数で調整可能。
 *    - Video セクション（.shopify-section--video）直下／または直後の Custom Multi Column 内の
 *      .c-custom-multi-column_root > .c-multi-column__item は init 時に自動で本クラスを付与する。
 *
 * prefers-reduced-motion: ブラーインは分割・演出なし。フェードブラーは CSS で即表示。
 * テーマエディタ: shopify:section:load で該当セクション配下を再バインドする。
 */

/** @type {IntersectionObserver|null} */
let cAnimateScrollRevealObserverBlurChars = null;

/** @type {IntersectionObserver|null} */
let cAnimateScrollRevealObserverFadeBlur = null;

/** 1文字ブラーイン: 従来どおりやや早め（下端を少し内側に） */
const C_ANIMATE_IO_BLUR_CHARS = { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.05 };

/**
 * フェードブラー: ビューポート上下を rootMargin で狭めた帯の内側に入り、かつ一定割合が見えてから発火。
 *（下端の一瞬の接触や、画面外ギリギリでの終了を防ぐ）
 */
const C_ANIMATE_IO_FADE_BLUR = { root: null, rootMargin: '-12% 0px -24% 0px', threshold: 0.22 };

function cAnimatePrefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Video ブロック直下にマルチカラムがネストされるケース、または Video の直後セクションが Custom Multi Column のケース */
const C_ANIMATE_VIDEO_MULTI_COLUMN_ITEM_SELECTOR =
  '.shopify-section--video .c-custom-multi-column_root > .c-multi-column__item, .shopify-section--video + .shopify-section--custom-multi-column .c-custom-multi-column_root > .c-multi-column__item';

/**
 * shopify-section--video とマルチカラムカードの組み合わせにパターン2（フェード＋ブラー）を適用するため .c-animate_fade-blur を付与する。
 * sibling はセクション編集で順序が変わるため document 全体から検索する。
 */
function cAnimateTagVideoContextMultiColumnFadeBlur() {
  document.querySelectorAll(C_ANIMATE_VIDEO_MULTI_COLUMN_ITEM_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.classList.add('c-animate_fade-blur');
  });
}

/**
 * @param {ParentNode} parent
 */
function cAnimateAppendBlurBreak(parent) {
  const lastChild = parent.lastChild;
  if (lastChild instanceof HTMLElement && lastChild.tagName === 'BR') return;
  parent.appendChild(document.createElement('br'));
}

/**
 * @param {ParentNode} parent
 * @param {string} char
 * @param {number} index
 * @param {number} safeStagger
 * @param {number} safeDuration
 * @returns {number}
 */
function cAnimateAppendBlurChar(parent, char, index, safeStagger, safeDuration) {
  if (char === '\n' || char === '\r') {
    cAnimateAppendBlurBreak(parent);
    return index;
  }

  const span = document.createElement('span');
  span.className = 'c-animate_blur-in-chars_char';
  span.textContent = char;
  span.style.opacity = '0';
  span.style.animation = `c-animate_blur-in-chars_keyframes ${safeDuration}s forwards`;
  span.style.animationDelay = `${index * safeStagger}s`;
  parent.appendChild(span);
  return index + 1;
}

/**
 * @param {ParentNode} parent
 * @param {string} text
 * @param {number} index
 * @param {number} safeStagger
 * @param {number} safeDuration
 * @returns {number}
 */
function cAnimateAppendBlurText(parent, text, index, safeStagger, safeDuration) {
  Array.from(text).forEach((char) => {
    index = cAnimateAppendBlurChar(parent, char, index, safeStagger, safeDuration);
  });
  return index;
}

/**
 * @param {ParentNode} parent
 * @param {Node[]} childNodes
 * @param {number} index
 * @param {number} safeStagger
 * @param {number} safeDuration
 * @returns {number}
 */
function cAnimateAppendBlurChildNodes(parent, childNodes, index, safeStagger, safeDuration) {
  childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      index = cAnimateAppendBlurText(parent, node.textContent || '', index, safeStagger, safeDuration);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE || !(node instanceof HTMLElement)) return;

    if (node.tagName === 'BR') {
      cAnimateAppendBlurBreak(parent);
      return;
    }

    const clone = node.cloneNode(false);
    parent.appendChild(clone);
    index = cAnimateAppendBlurChildNodes(clone, Array.from(node.childNodes), index, safeStagger, safeDuration);
  });
  return index;
}

/**
 * @param {Element} el
 */
function cAnimateActivateBlurInChars(el) {
  if (!(el instanceof HTMLElement)) return;
  if (el.dataset.cAnimateBlurInDone === 'true') return;

  if (cAnimatePrefersReducedMotion()) {
    el.dataset.cAnimateBlurInDone = 'true';
    return;
  }

  const fromAttr = el.getAttribute('data-c-animate-blur-text');
  const stagger = parseFloat(el.getAttribute('data-c-animate-blur-stagger') || '0.05');
  const duration = parseFloat(el.getAttribute('data-c-animate-blur-duration') || '0.8');
  const safeStagger = Number.isFinite(stagger) ? stagger : 0.05;
  const safeDuration = Number.isFinite(duration) ? duration : 0.8;

  const text = fromAttr != null && String(fromAttr).length > 0 ? String(fromAttr) : String(el.textContent || '');
  if (Array.from(text).length === 0) {
    el.dataset.cAnimateBlurInDone = 'true';
    return;
  }

  const childNodes = Array.from(el.childNodes);
  el.replaceChildren();

  if (fromAttr != null && String(fromAttr).length > 0) {
    cAnimateAppendBlurText(el, String(fromAttr), 0, safeStagger, safeDuration);
  } else {
    cAnimateAppendBlurChildNodes(el, childNodes, 0, safeStagger, safeDuration);
  }

  el.dataset.cAnimateBlurInDone = 'true';
}

/**
 * @param {Element} el
 */
function cAnimateActivateBlurIn(el) {
  if (!(el instanceof HTMLElement)) return;

  const duration = parseFloat(el.getAttribute('data-c-animate-blur-duration') || '');
  if (Number.isFinite(duration)) {
    el.style.setProperty('--c-animate-blur-in-duration', `${duration}s`);
  }

  el.classList.add('c-animate_is-in-view');
}

/**
 * @param {ParentNode|Document} [root]
 */
function initCAnimateScrollReveal(root = document) {
  cAnimateTagVideoContextMultiColumnFadeBlur();
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const blurNodes = scope.querySelectorAll(
    '.c-animate_blur-in-chars:not([data-c-animate-io-bound]), .c-animate_blur-in:not([data-c-animate-io-bound])'
  );
  /* fade-blur は Video 直後マルチカラムの自動付与があるため常に document から拾う（section:load で scope が video のみだと兄弟セクションが漏れる） */
  const fadeNodes = document.querySelectorAll('.c-animate_fade-blur:not([data-c-animate-io-bound])');

  if (blurNodes.length === 0 && fadeNodes.length === 0) {
    return;
  }

  if (!cAnimateScrollRevealObserverBlurChars) {
    cAnimateScrollRevealObserverBlurChars = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          cAnimateScrollRevealObserverBlurChars.unobserve(target);
          if (target.classList.contains('c-animate_blur-in-chars')) {
            cAnimateActivateBlurInChars(target);
            return;
          }

          if (target.classList.contains('c-animate_blur-in')) {
            cAnimateActivateBlurIn(target);
          }
        });
      },
      C_ANIMATE_IO_BLUR_CHARS
    );
  }

  if (!cAnimateScrollRevealObserverFadeBlur) {
    cAnimateScrollRevealObserverFadeBlur = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          cAnimateScrollRevealObserverFadeBlur.unobserve(target);
          if (target.classList.contains('c-animate_fade-blur')) {
            target.classList.add('c-animate_is-in-view');
          }
        });
      },
      C_ANIMATE_IO_FADE_BLUR
    );
  }

  blurNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.dataset.cAnimateIoBound = 'true';
    if (node.getAttribute('data-c-animate-immediate') === 'true') {
      if (node.classList.contains('c-animate_blur-in-chars')) {
        cAnimateActivateBlurInChars(node);
      } else if (node.classList.contains('c-animate_blur-in')) {
        cAnimateActivateBlurIn(node);
      }
      return;
    }
    cAnimateScrollRevealObserverBlurChars.observe(node);
  });

  fadeNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.dataset.cAnimateIoBound = 'true';
    cAnimateScrollRevealObserverFadeBlur.observe(node);
  });
}

initCAnimateScrollReveal(document);
document.addEventListener('DOMContentLoaded', () => initCAnimateScrollReveal(document));
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target && typeof target.querySelectorAll === 'function') {
    initCAnimateScrollReveal(target);
  }
});

/**
 * Custom Slideshow
 *
 * 1) 固定背景のConcept到達演出:
 *    Conceptパネルが画面下から入るほどCSS変数を0→1へ進め、黒40%オーバーレイとブラー量をCSS側で補間する。
 *
 * 2) 埋め込みStyling Slider:
 *    custom-slideshow.liquid 内だけの自前無限マーキー。Swiperには依存せず、translateをrequestAnimationFrameで動かす。
 */
function cCustomSlideshowClamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

let cCustomSlideshowProgressRaf = null;

function updateCustomSlideshowConceptProgress() {
  cCustomSlideshowProgressRaf = null;
  const viewportHeight = Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);

  document.querySelectorAll('[data-c-custom-slideshow]').forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    const concept = section.querySelector('[data-c-custom-slideshow-concept]');
    if (!(concept instanceof HTMLElement)) return;

    const rect = concept.getBoundingClientRect();
    const progress = cCustomSlideshowClamp((viewportHeight - rect.top) / (viewportHeight * 0.85));
    section.style.setProperty('--c-custom-slideshow-concept-progress', progress.toFixed(3));
  });
}

function scheduleCustomSlideshowConceptProgress() {
  if (cCustomSlideshowProgressRaf != null) return;
  cCustomSlideshowProgressRaf = window.requestAnimationFrame(updateCustomSlideshowConceptProgress);
}

function destroyCustomSlideshowSlider(el) {
  const controller = el && el.__cCustomSlideshowSlider;
  if (controller && typeof controller.destroy === 'function') {
    controller.destroy();
  }
  if (el instanceof HTMLElement) {
    delete el.__cCustomSlideshowSlider;
    delete el.dataset.cCustomSlideshowSliderInitialized;
  }
}

function initCustomSlideshowSlider(el) {
  if (!el || !(el instanceof HTMLElement)) return false;

  destroyCustomSlideshowSlider(el);

  const track = el.querySelector('[data-c-custom-slideshow-slider-track]');
  if (!(track instanceof HTMLElement)) return false;

  const duration = Math.max(Number(el.dataset.continuousSpeed) || 30000, 24000);
  let cycleWidth = 0;
  let offsetX = 0;
  let lastTime = 0;
  let rafId = null;
  let resizeTimer = null;
  let resumeTimer = null;
  let running = false;
  let isDragging = false;
  let hasSetInitialMobileOffset = false;
  let dragStartX = 0;
  let dragStartOffset = 0;

  const getOriginalSlides = () => {
    return Array.from(track.querySelectorAll('[data-c-custom-slideshow-slider-slide]:not([data-c-custom-slideshow-loop-clone])')).filter(
      (slide) => slide instanceof HTMLElement
    );
  };

  const clearRaf = () => {
    if (rafId == null) return;
    window.cancelAnimationFrame(rafId);
    rafId = null;
  };

  const clearResumeTimer = () => {
    if (resumeTimer == null) return;
    window.clearTimeout(resumeTimer);
    resumeTimer = null;
  };

  const normalizeOffset = (value) => {
    if (!cycleWidth) return 0;
    let next = value;

    while (next <= -cycleWidth) next += cycleWidth;
    while (next > 0) next -= cycleWidth;

    return next;
  };

  const render = () => {
    track.style.transform = `translate3d(${offsetX}px, 0, 0)`;
  };

  const tick = (time) => {
    if (!running) return;

    if (!lastTime) lastTime = time;

    const delta = Math.min(time - lastTime, 64);
    lastTime = time;
    offsetX = normalizeOffset(offsetX - (cycleWidth / duration) * delta);
    render();
    rafId = window.requestAnimationFrame(tick);
  };

  const start = () => {
    if (running || !cycleWidth) return;
    clearResumeTimer();
    running = true;
    lastTime = 0;
    clearRaf();
    rafId = window.requestAnimationFrame(tick);
  };

  const stop = () => {
    running = false;
    clearRaf();
    clearResumeTimer();
  };

  const scheduleRestart = () => {
    stop();
    resumeTimer = window.setTimeout(start, 2000);
  };

  const removeClones = () => {
    track.querySelectorAll('[data-c-custom-slideshow-loop-clone="true"]').forEach((clone) => clone.remove());
  };

  const makeClone = (slide) => {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-c-custom-slideshow-loop-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    track.appendChild(clone);
  };

  const rebuild = () => {
    const wasRunning = running;
    stop();
    removeClones();

    const originals = getOriginalSlides();
    if (originals.length === 0 || el.clientWidth <= 0) return false;

    originals.forEach(makeClone);

    const firstClone = track.querySelector('[data-c-custom-slideshow-loop-clone="true"]');
    if (!(firstClone instanceof HTMLElement)) return false;

    cycleWidth = firstClone.offsetLeft - originals[0].offsetLeft;
    if (!Number.isFinite(cycleWidth) || cycleWidth <= 0) return false;

    const minTrackWidth = el.clientWidth + cycleWidth * 2;
    let guard = 0;

    while (track.scrollWidth < minTrackWidth && guard < 20) {
      originals.forEach(makeClone);
      guard += 1;
    }

    if (!hasSetInitialMobileOffset && originals.length > 1 && window.matchMedia('(max-width: 699px)').matches) {
      const firstSlideWidth = originals[0].getBoundingClientRect().width;
      const secondSlideOffset = originals[1].offsetLeft - originals[0].offsetLeft;
      offsetX = normalizeOffset((el.clientWidth - firstSlideWidth) / 2 - secondSlideOffset);
      hasSetInitialMobileOffset = true;
    } else {
      offsetX = normalizeOffset(offsetX);
    }

    render();

    if (wasRunning) start();

    return true;
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;

    stop();
    isDragging = true;
    dragStartX = event.clientX;
    dragStartOffset = offsetX;
    el.classList.add('is-dragging');

    if (typeof el.setPointerCapture === 'function') {
      try {
        el.setPointerCapture(event.pointerId);
      } catch (e) {}
    }
  };

  const onPointerMove = (event) => {
    if (!isDragging) return;

    offsetX = normalizeOffset(dragStartOffset + event.clientX - dragStartX);
    render();
  };

  const onPointerUp = (event) => {
    if (!isDragging) return;

    isDragging = false;
    el.classList.remove('is-dragging');

    if (typeof el.releasePointerCapture === 'function') {
      try {
        el.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    scheduleRestart();
  };

  const onWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    event.preventDefault();
    stop();
    offsetX = normalizeOffset(offsetX - event.deltaX);
    render();
    scheduleRestart();
  };

  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      rebuild();
      start();
    }, 120);
  };

  if (!rebuild()) return false;

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerUp);
  el.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('resize', onResize);
  start();

  el.__cCustomSlideshowSlider = {
    destroy() {
      stop();
      window.clearTimeout(resizeTimer);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      el.classList.remove('is-dragging');
      removeClones();
      track.style.transform = '';
      cycleWidth = 0;
      offsetX = 0;
    },
  };

  el.dataset.cCustomSlideshowSliderInitialized = 'true';

  return true;
}

function initCustomSlideshowSliders(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  scope.querySelectorAll('[data-c-custom-slideshow-slider-root]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.cCustomSlideshowSliderInitialized === 'true' && el.__cCustomSlideshowSlider) return;

    let attempts = 0;
    const tryInit = () => {
      if (!document.documentElement.contains(el)) return;
      if (initCustomSlideshowSlider(el)) return;
      attempts += 1;
      if (attempts < 240) window.requestAnimationFrame(tryInit);
    };
    tryInit();
  });
}

function initCustomSlideshow(root = document) {
  initCustomSlideshowSliders(root);
  scheduleCustomSlideshowConceptProgress();
}

initCustomSlideshow(document);
window.addEventListener('scroll', scheduleCustomSlideshowConceptProgress, { passive: true });
window.addEventListener('resize', scheduleCustomSlideshowConceptProgress);
document.addEventListener('DOMContentLoaded', () => initCustomSlideshow(document));
document.addEventListener('shopify:section:load', (event) => {
  initCustomSlideshow(event.target || document);
});
document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (!target || typeof target.querySelectorAll !== 'function') return;
  target.querySelectorAll('[data-c-custom-slideshow-slider-root]').forEach(destroyCustomSlideshowSlider);
});
